/**
 * CRM 2.0 / Batch-13 — Paket D: Wiedervorlage v2 (Historie, Setting, Erledigt).
 *
 * Getrennt von `src/lib/v1.ts` (dort leben weiterhin `v2Users.setWiedervorlage`
 * für Setzen/Löschen des Datums und `v2Wiedervorlage.faellig` für den
 * globalen Fälligkeits-Filter — beide unverändert) und von
 * `src/pages/pflege/wiedervorlage.ts` (Fälligkeits-Zustand/Farbgebung,
 * reine UI-Logik ohne Netzwerk). Diese Datei bündelt nur die neuen
 * Paket-D-Endpunkte: Historie je Anfrage, das Admin-Setting
 * `wiedervorlage_max` und "als erledigt markieren".
 *
 * `wiedervorlageMaxApi` liest/schreibt gezielt NUR den Key
 * `wiedervorlage_max` auf `GET/PUT /v2/admin/system-settings` — bewusst
 * ohne den generischen `systemSettingsApi` aus `src/lib/api.ts`
 * anzufassen (der wird parallel von anderer Stelle gepflegt). Das Backend
 * validiert die übrigen Felder mit `sometimes`, ein PUT mit nur
 * `wiedervorlage_max` lässt timezone/currency/company_name unangetastet.
 */
import { apiV2 } from './api';

export interface WiedervorlageEntry {
  id: number;
  nr: number;
  /** Y-m-d — bewusst als reiner String behandelt (Bug B3: kein new Date()-TZ-Shift). */
  faellig_am: string;
  /**
   * Befund 23.09.: "H:i" (z.B. "14:30") oder null. Null bedeutet "keine
   * Uhrzeit / ganztägig" — derselbe Zustand wie ein im Formular auf "00:00"
   * stehendes Zeitfeld (ein einziger kanonischer Wert, s. Backend-Migration
   * 2026_09_23_110000).
   */
  faellig_um: string | null;
  notiz: string | null;
  /** Y-m-d oder null, solange die Wiedervorlage offen ist. */
  erledigt_am: string | null;
  admin_id: number | null;
  created_at: string | null;
}

export interface WiedervorlageHistory {
  data: WiedervorlageEntry[];
  max: number;
  count: number;
}

/**
 * GET /v2/admin/users/{id}/wiedervorlagen — Historie (neueste zuerst) + max + count.
 * Für den Zähler "n/N" im Drawer und die aufklappbare Verlaufsliste.
 */
export async function wiedervorlageHistory(userId: number): Promise<WiedervorlageHistory> {
  const res = await apiV2.get<{ data: WiedervorlageEntry[]; meta: { max: number; count: number } }>(
    `/admin/users/${userId}/wiedervorlagen`,
  );
  return { data: res.data.data, max: res.data.meta.max, count: res.data.meta.count };
}

export interface WiedervorlageSaveResult {
  id: number;
  wiedervorlage_am: string | null;
  wiedervorlage_notiz: string | null;
  /** Befund 23.09. — "H:i" oder null (= keine Uhrzeit / ganztägig). */
  wiedervorlage_um: string | null;
  wiedervorlage_nr: number | null;
  wiedervorlage_max: number;
}

/**
 * PATCH /v2/admin/users/{id}/wiedervorlage — { erledigt: true }.
 * Setzt `erledigt_am` der aktuell offenen Wiedervorlage-Zeile. 422
 * (E_WIEDERVORLAGE_NONE_OPEN), wenn keine Zeile offen ist.
 */
export async function markWiedervorlageErledigt(userId: number): Promise<WiedervorlageSaveResult & { erledigt_am: string | null }> {
  const res = await apiV2.patch<{ data: WiedervorlageSaveResult & { erledigt_am: string | null } }>(
    `/admin/users/${userId}/wiedervorlage`,
    { erledigt: true },
  );
  return res.data.data;
}

/**
 * PATCH /v2/admin/wiedervorlagen/{id} — bearbeitet einen konkreten
 * Historien-Eintrag (Datum/Notiz), unabhängig davon ob er die aktuell
 * offene Zeile ist. Für den "Verlauf" im Drawer: Klick auf einen Eintrag
 * lädt Datum+Notiz ins Formular, Speichern ruft dies auf.
 */
export async function updateWiedervorlageEntry(
  entryId: number,
  payload: { faellig_am?: string; faellig_um?: string | null; notiz?: string | null },
): Promise<WiedervorlageEntry> {
  const res = await apiV2.patch<{ data: WiedervorlageEntry }>(`/admin/wiedervorlagen/${entryId}`, payload);
  return res.data.data;
}

/**
 * DELETE /v2/admin/wiedervorlagen/{id} — löscht einen Historien-Eintrag
 * (inkl. verknüpfter Notiz/Kalendertermin serverseitig). Der users-Spiegel
 * wird serverseitig aus der letzten offenen Zeile neu gesetzt.
 */
export async function deleteWiedervorlageEntry(entryId: number): Promise<void> {
  await apiV2.delete(`/admin/wiedervorlagen/${entryId}`);
}

/**
 * PATCH /v2/admin/wiedervorlagen/{id}/erledigt — markiert GENAU diesen
 * Verlaufs-Eintrag als erledigt (anders als `markWiedervorlageErledigt`,
 * das immer die zuletzt offene Zeile des Users sucht).
 */
export async function markWiedervorlageEntryErledigt(entryId: number): Promise<WiedervorlageEntry> {
  const res = await apiV2.patch<{ data: WiedervorlageEntry }>(`/admin/wiedervorlagen/${entryId}/erledigt`);
  return res.data.data;
}

/** Admin-Setting `wiedervorlage_max` (Einstellungen → Sonstiges). Default 5, Bereich 1..20. */
export const wiedervorlageMaxApi = {
  get: async (): Promise<number> => {
    const res = await apiV2.get<{ data: { wiedervorlage_max: number } }>('/admin/system-settings');
    return res.data.data.wiedervorlage_max;
  },

  update: async (value: number): Promise<number> => {
    const res = await apiV2.put<{ data: { wiedervorlage_max: number } }>(
      '/admin/system-settings',
      { wiedervorlage_max: value },
    );
    return res.data.data.wiedervorlage_max;
  },
};
